import { useState } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useInView } from 'react-cool-inview'

import { ArcadeButton, spacing, Stack } from '@cash-design-system/react'
import { DynamicDisclosureLayout } from '@cash-web/dynamic-disclosure'
import { DisclosureBlocker } from '@cash-web/protos/squareup/franklin/blockers/disclosure_blocker.pb'
import { useMutationConfirmDisclosure } from '@cash-web/shared-data-access-api/react-query'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BlockerProps } from '../..'

interface Props extends BlockerProps {
  blocker: DisclosureBlocker
}

const DisclosureModalContentContainer = styled.div`
  overflow-y: auto;
  max-height: 60vh;
  position: relative;
  max-width: ${({ theme }) => theme.maxWidths.content};
`

const StickyFooter = styled.div<{ isInView?: boolean }>`
  bottom: ${props => props.theme.pxToRem(-1)};
  background: ${props => props.theme.arcadeColors.component.modalBackground};
  padding-top: ${spacing.m};
  position: sticky;

  ${({ isInView, theme }) =>
    isInView
      ? css`
          border-top: 1px solid ${theme.colors.hairline};
        `
      : css`
          box-shadow: ${theme.dropShadows.bottomBar};
        `}
`

const ObservationPixel = styled.div`
  display: inline-block;
  height: ${props => props.theme.pxToRem(1)};
  position: absolute;
  transform: translateY(-${spacing.m});
  width: 100%;
`

export const BlockerDisclosure = ({ blocker }: Props) => {
  const flow = useFlowContext()
  const { mutateAsync: confirmDisclosure, isLoading } = useMutationConfirmDisclosure()

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const { observe, inView } = useInView({ threshold: 1, onEnter: () => setHasScrolledToBottom(true) })
  const disableConfirmButton = !hasScrolledToBottom

  const handleSubmit = async () => {
    flow.updateWithResponse({
      response: confirmDisclosure({
        request_context: {},
      }),
      queriesToInvalidate: [],
    })
  }

  return (
    <DisclosureModalContentContainer>
      <Stack space={spacing.xl}>
        <DynamicDisclosureLayout disclosure={blocker} />

        <StickyFooter isInView={inView}>
          <ArcadeButton
            size="cta"
            prominence="prominent"
            disabled={disableConfirmButton}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {disableConfirmButton ? blocker?.scroll_button_text : blocker?.accept_button_text}
          </ArcadeButton>
        </StickyFooter>
      </Stack>
      <ObservationPixel ref={observe} data-testid="scroll-to" />
    </DisclosureModalContentContainer>
  )
}
